import { useIsTouch } from "@/hooks/useIsTouch";
import { Input } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SearchSuggestionList, type Action } from "./search-suggestions";
import {
  SEARCH_PLACEHOLDER,
  useSearchSuggestions
} from "./useSearchSuggestions";

/**
 * The header search box.
 *
 * Two surfaces, one behaviour: on a fine pointer this is a live input with an
 * anchored dropdown, and on a coarse one it is a button that opens the
 * full-screen search route instead. The phone case is not a styling variant of
 * the dropdown - a panel anchored to a `max-w-xl` bar sits behind the on-screen
 * keyboard, which is what storefront#158 exists to fix - so it does not try to
 * be one. Everything that is genuinely shared (the query, the grouped
 * suggestions, what Enter does) lives in {@link useSearchSuggestions}.
 */
function ActionSearchBar() {
  const isTouch = useIsTouch();

  // The trigger and the live box are different elements with different
  // semantics (a button vs a text field), so which one renders is decided
  // here rather than by toggling props on one of them.
  return isTouch ? <SearchRouteTrigger /> : <SearchDropdown />;
}

/**
 * The coarse-pointer entry point: a button that looks like the input it
 * replaces and opens `/search/input`.
 *
 * Deliberately not a focusable text field. A field here would summon the
 * keyboard over the anchored dropdown - the defect this route replaces - and
 * an input the buyer can type into but whose suggestions they cannot see is
 * worse than one they cannot type into at all.
 */
function SearchRouteTrigger() {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate({ to: "/search/input" })}
      // Named for what it does, not what it says: the visible text is the
      // placeholder it stands in for, which is a hint, not a label.
      aria-label="Search"
      className="flex h-9 w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 text-left text-sm text-gray-500 transition-colors hover:bg-gray-100 dark:border-gray-800 dark:bg-zinc-900 dark:text-gray-400"
    >
      <Search className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="truncate">{SEARCH_PLACEHOLDER}</span>
    </button>
  );
}

/**
 * The phone header's search entry point: a compact icon button that opens the
 * `/search/input` route.
 *
 * Below `md` the header row has no room for the full search bar beside the brand
 * and the action icons, and on a phone that bar is only ever a trigger for this
 * route anyway (see {@link SearchRouteTrigger}) - so the header collapses it to
 * this icon rather than spending a whole line on a box the buyer can't type
 * suggestions into. Same destination as the trigger, one tap.
 */
function MobileSearchButton() {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate({ to: "/search/input" })}
      aria-label="Search"
      className="flex h-11 w-11 items-center justify-center rounded-lg text-neutral-800 transition-colors hover:bg-gray-50 dark:text-neutral-200"
    >
      <Search className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

/** The fine-pointer box: a live input with the dropdown anchored under it. */
function SearchDropdown() {
  const [isFocused, setIsFocused] = useState(false);
  // The blur-to-close timer. Held in a ref so it can be cleared on unmount -
  // otherwise it fires ~200ms later and calls setState on a gone component (a
  // React post-unmount warning in the app, an "after teardown" error in tests).
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useSearchSuggestions({
    active: isFocused,
    onNavigated: () => setIsFocused(false)
  });

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleFocus = () => setIsFocused(true);

  // Close a beat after blur, so a click on a result lands before it is hidden.
  const handleBlur = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 200);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Esc dismisses the panel without touching the query - a concern of this
    // surface alone, so it is handled before the shared handler sees the key.
    if (event.key === "Escape") {
      event.preventDefault();
      setIsFocused(false);
      return;
    }
    search.handleKeyDown(event);
  };

  // The panel's own open/close animation. It runs when the panel mounts and
  // unmounts, not when its contents change - `staggerChildren` and the matching
  // per-row variants used to be here too, and they are what made every
  // keystroke animate a dozen rows out while a dozen more animated in. See
  // `SearchSuggestionList`.
  const container = {
    hidden: { opacity: 0, height: 0 },
    show: {
      opacity: 1,
      height: "auto",
      transition: { height: { duration: 0.4 } }
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: { height: { duration: 0.3 }, opacity: { duration: 0.2 } }
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        <div className="w-full max-w-xl">
          <div className="relative">
            <Input
              type="text"
              placeholder={SEARCH_PLACEHOLDER}
              value={search.inputValue}
              onChange={(e) => search.setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="h-9 rounded-lg py-1.5 pl-3 pr-9 text-sm focus-visible:ring-offset-0"
            />
            <div className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2">
              <AnimatePresence mode="popLayout">
                {search.inputValue.length > 0 ? (
                  <motion.div
                    key="send"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Send className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="search"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isFocused && search.actions && (
            <motion.div
              className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border bg-white shadow-lg dark:border-gray-800 dark:bg-black"
              variants={container}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              <SearchSuggestionList
                actions={search.actions}
                highlightedIndex={search.highlightedIndex}
                onHighlight={search.setHighlightedIndex}
                onSelect={search.selectAction}
                isTouch={false}
              />
              {/* The arrow-key/Enter/Esc legend. This surface only renders on a
                  fine pointer now, so the keys it names are always present. */}
              <div className="mt-2 border-t border-gray-100 px-3 py-2 dark:border-gray-800">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>↑↓ to navigate • Enter to select</span>
                  <span>ESC to close</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export { ActionSearchBar, MobileSearchButton };
export type { Action };
